import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
    ArrowRight,
    Clock,
    MapPin,
    Bike,
    Flame,
    Wallet,
    ShoppingBag,
} from "lucide-react";
import { Reveal, MaskedLine } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { SHOP, IMG, FAVORITES, MARQUEE_ITEMS } from "@/data/site";
import { trackEvent } from "@/lib/analytics";

function isOpenNow() {
    const hour = new Date().getHours();
    return hour >= 11 && hour < 21;
}

function Hero() {
    const imgRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: imgRef,
        offset: ["start start", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], [0, 60]);
    const open = isOpenNow();

    return (
        <section className="relative overflow-hidden pb-16 pt-28 lg:pb-24 lg:pt-36">
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-24 right-[-10%] h-[34rem] w-[34rem] rounded-full bg-accent/5 blur-3xl"
            />
            <div
                aria-hidden="true"
                className="pointer-events-none absolute bottom-[-8rem] left-[-8rem] h-[26rem] w-[26rem] rounded-full bg-matcha/10 blur-3xl"
            />

            <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-12 lg:gap-10 lg:px-8">
                <div className="lg:col-span-6">
                    <Reveal>
                        <span className="eyebrow">Cafe — Bandar Lampung</span>
                    </Reveal>

                    <h1 className="mt-5 font-display text-5xl font-extrabold tracking-tighter leading-[1.05] text-ink sm:text-6xl lg:text-7xl">
                        <MaskedLine delay={0.05}>Good coffee,</MaskedLine>
                        <MaskedLine delay={0.18}>
                            <span className="text-accent">good mood,</span>
                        </MaskedLine>
                        <MaskedLine delay={0.31}>every day.</MaskedLine>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.55, duration: 0.8 }}
                        className="mt-6 max-w-xl text-base leading-relaxed text-ink-muted sm:text-lg"
                    >
                        Yuhuku Kopi adalah cafe kecil di Perum Raffles
                        Residence. Dari espresso klasik sampai matcha yang
                        creamy, semuanya dibuat langsung saat kamu pesan —
                        mampir, atau dibungkus buat jalan.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.7, duration: 0.8 }}
                        className="mt-9 flex flex-wrap items-center gap-3.5"
                    >
                        <Link
                            to="/menu"
                            data-testid="hero-cta-menu"
                            className="btn-primary"
                        >
                            Lihat Menu <ArrowRight className="h-4 w-4" />
                        </Link>
                        <a
                            href={SHOP.gofood}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-cta-gofood"
                            onClick={() =>
                                trackEvent("cta_click", { cta: "gofood_hero" })
                            }
                            className="btn-secondary"
                        >
                            <ShoppingBag className="h-4 w-4" /> Pesan di GoFood
                        </a>
                    </motion.div>

                    <motion.ul
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.9, duration: 0.8 }}
                        className="mt-9 flex flex-wrap gap-x-8 gap-y-3 text-sm text-ink-muted"
                    >
                        <li className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-accent" />
                            Buka setiap hari 11.00–21.00
                        </li>
                        <li className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-accent" />
                            Raffles Residence Blok C3-04
                        </li>
                        <li className="flex items-center gap-2">
                            <Bike className="h-4 w-4 text-accent" />
                            Tersedia di GoFood
                        </li>
                    </motion.ul>
                </div>

                <div ref={imgRef} className="lg:col-span-6">
                    <motion.div
                        style={{ y }}
                        className="relative mx-auto max-w-md lg:max-w-none"
                    >
                        <div className="overflow-hidden rounded-[2.5rem] border border-ink/5 shadow-lift">
                            <img
                                src={IMG.hero}
                                alt="Espresso susu dingin khas Yuhuku Kopi dengan plang logo"
                                className="aspect-[4/5] w-full object-cover"
                            />
                        </div>
                        <div
                            data-testid="hero-chip-status"
                            className="chip absolute -left-4 top-8 sm:-left-8"
                        >
                            <span
                                className={`inline-block h-2.5 w-2.5 rounded-full ${
                                    open ? "bg-matcha" : "bg-accent"
                                }`}
                            />
                            {open ? "Sedang buka" : "Buka jam 11.00"}
                        </div>
                        <div
                            data-testid="hero-chip-signature"
                            className="chip absolute -right-3 bottom-16 sm:-right-6"
                        >
                            Signature — 15K
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}

function Favorites() {
    return (
        <section className="py-20 md:py-28" data-testid="home-favorites">
            <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
                <div className="lg:col-span-4">
                    <div className="lg:sticky lg:top-28">
                        <Reveal>
                            <span className="eyebrow">House favorites</span>
                            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-ink sm:text-5xl">
                                Always a good call.
                            </h2>
                            <p className="mt-5 max-w-sm leading-relaxed text-ink-muted">
                                Ini beberapa menu yang paling sering dipesan di
                                Yuhuku. Semua harga bersahabat, mulai dari 13K.
                            </p>
                            <Link
                                to="/menu"
                                data-testid="favorites-cta-menu"
                                className="btn-ghost mt-7 inline-flex"
                            >
                                Lihat Semua Menu <ArrowRight className="h-4 w-4" />
                            </Link>
                        </Reveal>
                    </div>
                </div>

                <div className="flex flex-col gap-4 lg:col-span-8">
                    {FAVORITES.map((f, i) => (
                        <Reveal key={f.name} delay={i * 0.07}>
                            <div
                                data-testid={`favorite-item-${f.name.toLowerCase().replace(/\s+/g, "-")}`}
                                className="group flex items-center gap-5 rounded-3xl border border-ink/5 bg-white p-4 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift sm:gap-6 sm:p-5"
                            >
                                <img
                                    src={f.image}
                                    alt={f.name}
                                    loading="lazy"
                                    className="h-20 w-20 shrink-0 rounded-2xl object-cover sm:h-24 sm:w-24"
                                />
                                <div className="min-w-0 flex-1">
                                    <h3 className="font-display text-lg font-bold tracking-tight text-ink sm:text-xl">
                                        {f.name}
                                    </h3>
                                    <p className="mt-1 line-clamp-2 text-sm text-ink-muted">
                                        {f.note}
                                    </p>
                                </div>
                                <span className="shrink-0 font-display text-xl font-extrabold text-accent sm:text-2xl">
                                    {f.price}
                                </span>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}

function Story() {
    return (
        <section className="py-20 md:py-28" data-testid="home-story">
            <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
                <Reveal className="relative order-2 lg:order-1">
                    <div className="overflow-hidden rounded-[2rem] border border-ink/5 shadow-soft">
                        <img
                            src={IMG.shopInterior}
                            alt="Counter cafe Yuhuku Kopi dengan tulisan Pesan dan Bayar di sini"
                            loading="lazy"
                            className="aspect-[4/3] w-full object-cover"
                        />
                    </div>
                    <div className="absolute -bottom-8 -right-2 w-36 overflow-hidden rounded-3xl border-4 border-bg shadow-lift sm:-right-6 sm:w-48">
                        <img
                            src={IMG.shopExterior}
                            alt="Etalase depan cafe Yuhuku Kopi"
                            loading="lazy"
                            className="aspect-square w-full object-cover"
                        />
                    </div>
                </Reveal>

                <div className="order-1 lg:order-2">
                    <Reveal>
                        <span className="eyebrow">Cerita kami</span>
                        <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-ink sm:text-5xl">
                            Brewed in Bandar Lampung.
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="mt-6 max-w-lg leading-relaxed text-ink-muted">
                            Yuhuku Kopi adalah cafe kecil di Perum Raffles
                            Residence yang sehari-hari ramai oleh warga
                            sekitar. Namanya unik dan jarang dilupakan — begitu
                            juga kopinya.
                        </p>
                        <p className="mt-4 max-w-lg leading-relaxed text-ink-muted">
                            Mulai dari espresso klasik sampai matcha yang
                            creamy, tiap gelas dibuat langsung saat kamu pesan.
                            Duduk santai di cafe, atau bungkus buat teman
                            perjalananmu.
                        </p>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <ul className="mt-8 flex flex-col gap-3">
                            {[
                                {
                                    icon: Flame,
                                    text: "Biji kopi & matcha premium, dibuat fresh di depan kamu",
                                },
                                {
                                    icon: Wallet,
                                    text: "Harga bersahabat — menu mulai dari 13K",
                                },
                                {
                                    icon: Bike,
                                    text: "Mager? Tinggal pesan lewat GoFood",
                                },
                            ].map(({ icon: Icon, text }) => (
                                <li
                                    key={text}
                                    className="flex items-center gap-3.5 rounded-2xl border border-ink/5 bg-white px-5 py-3.5 text-sm font-medium text-ink shadow-soft"
                                >
                                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-matcha/15">
                                        <Icon className="h-4 w-4 text-matcha" />
                                    </span>
                                    {text}
                                </li>
                            ))}
                        </ul>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}

function VisitCta() {
    return (
        <section className="px-4 pb-20 sm:px-6 md:pb-28 lg:px-8" data-testid="home-visit">
            <Reveal className="mx-auto max-w-7xl">
                <div className="overflow-hidden rounded-[2.5rem] bg-ink px-6 py-16 text-bg sm:px-12 lg:px-16 lg:py-20">
                    <div className="max-w-2xl">
                        <span className="text-xs font-bold uppercase tracking-[0.2em] text-accent">
                            Mampir kapan hari
                        </span>
                        <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight sm:text-5xl">
                            See you at the shop.
                        </h2>
                        <p className="mt-5 leading-relaxed text-bg/70">
                            {SHOP.address} — buka setiap hari pukul 11.00 sampai
                            21.00 WIB. Mampir, atau kirim pesan dulu kalau mau
                            pesan banyak.
                        </p>
                    </div>
                    <div className="mt-9 flex flex-wrap gap-3.5">
                        <a
                            href={SHOP.mapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="visit-cta-maps"
                            className="btn-primary"
                        >
                            <MapPin className="h-4 w-4" /> Petunjuk Arah
                        </a>
                        <Link
                            to="/kontak"
                            data-testid="visit-cta-kontak"
                            className="rounded-full border border-white/25 px-7 py-3.5 text-sm font-semibold text-bg transition-all hover:-translate-y-0.5 hover:border-white/50"
                        >
                            Kirim Pesan <ArrowRight className="inline h-4 w-4" />
                        </Link>
                    </div>
                </div>
            </Reveal>
        </section>
    );
}

export default function Home() {
    return (
        <>
            <Hero />
            <Marquee items={MARQUEE_ITEMS} />
            <Favorites />
            <Story />
            <VisitCta />
        </>
    );
}